// P9.2A — OPTIONAL calendar capability pack.
//
// Deterministic calendar/clock concepts (month-end windows, turn-of-month windows, local
// clock windows, first-bar-of-day references) for flow/seasonality propositions
// (month-end rebalancing, turn-of-month contributions, overnight/intraday split).
//
// Like every pack: these names are NOT core ontology. They compile down to generic
// single-bar events; the core engine runs identically without this pack, and a setup that
// references 'calendar:<concept>' without it raises UnsupportedCapabilityError (counted).
//
// All date math is deterministic UTC arithmetic + the explicit TimezoneSpec DST rules from
// multiTimeframe.ts (no Intl, no ambient locale, replayable).

import type { StructuralEvent } from '../structuralPrimitives';
import { inSessionWindow, isDst, TZ_LONDON, TZ_NEW_YORK, TZ_UTC, type TimezoneSpec } from '../multiTimeframe';
import type { CapabilityPack } from './registry';

const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const str = (v: unknown, d: string): string => (typeof v === 'string' ? v : d);
const TZ: Record<string, TimezoneSpec> = { TZ_NEW_YORK, TZ_LONDON, TZ_UTC };

/** Local calendar (year, month0, day) for a UTC instant under a timezone spec. */
function localDate(utcMs: number, tz: TimezoneSpec): { y: number; m0: number; day: number } {
  const off = (isDst(utcMs, tz) ? tz.dstOffsetMin : tz.stdOffsetMin) * 60_000;
  const d = new Date(utcMs + off);
  return { y: d.getUTCFullYear(), m0: d.getUTCMonth(), day: d.getUTCDate() };
}
const daysInMonth = (y: number, m0: number): number => new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();

const ev = (concept: string, i: number): StructuralEvent =>
  ({ concept: concept as StructuralEvent['concept'], version: 1, refIndex: i, confirmedAt: i, direction: 'neutral' });

export const CALENDAR_PACK: CapabilityPack = {
  id: 'calendar',
  version: 1,
  concepts: {
    month_end_window: {
      describe: 'bar-open falls within the last `lastDays` local calendar days of the month (deterministic tz/DST)', version: 1,
      makeFeed: (bars, p) => {
        const tz = TZ[str(p.tz, 'TZ_UTC')] ?? TZ_UTC;
        const lastDays = num(p.lastDays, 3);
        return (i) => {
          const { y, m0, day } = localDate(bars[i].timestamp, tz);
          return day > daysInMonth(y, m0) - lastDays ? ev('month_end_window', i) : null;
        };
      },
    },
    turn_of_month_window: {
      describe: 'bar-open falls in [last `before` days of month .. first `after` days of next] (deterministic tz/DST)', version: 1,
      makeFeed: (bars, p) => {
        const tz = TZ[str(p.tz, 'TZ_UTC')] ?? TZ_UTC;
        const before = num(p.before, 1), after = num(p.after, 3);
        return (i) => {
          const { y, m0, day } = localDate(bars[i].timestamp, tz);
          const inWindow = day > daysInMonth(y, m0) - before || day <= after;
          return inWindow ? ev('turn_of_month_window', i) : null;
        };
      },
    },
    clock_window: {
      describe: 'bar-open falls in an exchange-local clock window [startMin,endMin) minutes; supports overnight (start>end); DST-correct', version: 1,
      makeFeed: (bars, p) => {
        const tz = TZ[str(p.tz, 'TZ_UTC')] ?? TZ_UTC;
        const s = num(p.startMin, 0), e = num(p.endMin, 1440);
        return (i) => inSessionWindow(bars[i].timestamp, tz, s, e) ? ev('clock_window', i) : null;
      },
    },
    first_bar_of_local_day: {
      describe: 'first bar whose local calendar day differs from the prior bar (deterministic day-roll reference)', version: 1,
      makeFeed: (bars, p) => {
        const tz = TZ[str(p.tz, 'TZ_UTC')] ?? TZ_UTC;
        return (i) => {
          if (i === 0) return ev('first_bar_of_local_day', 0);
          const a = localDate(bars[i - 1].timestamp, tz), b = localDate(bars[i].timestamp, tz);
          return a.day !== b.day || a.m0 !== b.m0 || a.y !== b.y ? ev('first_bar_of_local_day', i) : null;
        };
      },
    },
  },
};
